/**
 * The tutorial's vocabulary: four kinds of step, and what it means for a move to satisfy
 * one.
 *
 * The 2020 tutorial was a separate Node process that logged in as a privileged bot,
 * created a real match, and mutated its state over the wire with a `resetPartial`
 * dispatcher that existed for no other reason. Every line of dialogue was a network round
 * trip, the server carried a `'tutorial'` formation and a god-mode account to support it,
 * and by the 2023 port the whole thing no longer ran at all.
 *
 * This is the same script as data, interpreted against a local engine. No server, no
 * account, no match record, instant reset, works offline — and the special cases it
 * needed are already gone from the server rather than merely unused.
 */

import type { Move, PieceCode } from '@gok/engine';

/** A `"q,r,s"` coordinate key. Never a cell index; see CLAUDE.md. */
export type HexKey = string;

/** Pieces to place, by coordinate. `reset` clears the board first. */
export interface Setup {
  readonly cells: Readonly<Record<HexKey, PieceCode>>;
  readonly reset?: boolean;
  /** Spawns in hand, per player. Defaults to leaving them as they are. */
  readonly spawns?: readonly [number, number];
}

/**
 * What the learner has to play to move on.
 *
 * Every field narrows; an empty pattern accepts any legal move. `glide` is the one that
 * needs the board to answer it, because a bow shot and an ordinary step are the same kind
 * of move and differ only in how far they travel (§4.1).
 */
export interface MovePattern {
  readonly type?: Move['type'];
  readonly from?: HexKey;
  readonly to?: HexKey;
  /** Any one of these destinations will do. */
  readonly toAny?: readonly HexKey[];
  /** The move must take something. */
  readonly captures?: boolean;
  /** The move must travel further than one cell — that is, it must be a bow shot. */
  readonly glide?: boolean;
}

export interface SayStep {
  readonly say: string;
}

export interface SetupStep {
  readonly setup: Setup;
}

export interface AwaitStep {
  readonly await: MovePattern;
  /** Cells to ring when the learner needs a push. */
  readonly hint?: readonly HexKey[];
  /** What to say when they wander. Shown with the hint. */
  readonly nudge?: string;
  /** How long to wait before showing the hint unasked. */
  readonly nudgeAfterMs?: number;
}

export interface WaitStep {
  readonly wait: number;
}

export type Step = SayStep | SetupStep | AwaitStep | WaitStep;

export const isSay = (s: Step): s is SayStep => 'say' in s;
export const isSetup = (s: Step): s is SetupStep => 'setup' in s;
export const isWait = (s: Step): s is WaitStep => 'wait' in s;
export const isAwait = (s: Step): s is AwaitStep => 'await' in s;

export interface MoveContext {
  /** Resolve a coordinate key to a cell index on the tutorial's board. */
  readonly indexOf: (key: HexKey) => number | undefined;
  /** Whether `from` and `to` are neighbours, for telling a step from a glide. */
  readonly adjacent: (from: number, to: number) => boolean;
  /** What stood on a cell *before* the move. */
  readonly occupantBefore: (cell: number) => number;
}

/** Whether a move satisfies a pattern. */
export function satisfies(pattern: MovePattern, move: Move, ctx: MoveContext): boolean {
  if (pattern.type && move.type !== pattern.type) return false;
  if (move.type === 'pass' || move.type === 'resign') {
    // Only reachable if a pattern asked for one, which no step does.
    return pattern.type === move.type;
  }

  if (pattern.from !== undefined && ctx.indexOf(pattern.from) !== move.fromIndex) {
    return false;
  }
  if (pattern.to !== undefined && ctx.indexOf(pattern.to) !== move.toIndex) return false;

  if (pattern.toAny && !pattern.toAny.some((key) => ctx.indexOf(key) === move.toIndex)) {
    return false;
  }

  if (pattern.captures !== undefined) {
    const took = ctx.occupantBefore(move.toIndex) !== 0;
    if (took !== pattern.captures) return false;
  }

  if (pattern.glide !== undefined) {
    const glided = move.type === 'movePiece' &&
      !ctx.adjacent(move.fromIndex, move.toIndex);
    if (glided !== pattern.glide) return false;
  }

  return true;
}
